"use client";
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useEffect } from 'react';
import { ListItem } from './ListItem.web.js';
import { container, list, appender } from './styles.js';
import { Box } from '../../../Box/Box.web.js';
import { ThemeProvider } from 'styled-components';
import { useTheme } from '../../theming/useTheme.js';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsoMorphicLayoutEffect.web.js';
import { ListItemsHeader } from './ListItemsHeader.web.js';
import { useHighlightActive } from '../../hooks/useHighlightActive.web.js';
import { px2rem } from '../../helpers/style.js';

var _excluded = ["enableJumpToLetter", "showItems", "highlightValue", "id", "items", "onSelect", "onClose", "onActiveChange", "opened", "selected", "offset", "disableFocusTransfer", "color", "onKeyboardNavigateTopOut", "disableBorders", "boxShadow", "highlightSelected", "aria-multiselectable", "aria-labelledby", "aria-label", "role", "activateFirstItem", "selectWithSpacebar", "renderItem", "disableActive", "maxHeight", "listItemRole", "position", "variant", "containerProps", "size", "header"];
function getActiveIndex(items) {
  return function (active) {
    return items.findIndex(function (item) {
      return item.value === active;
    });
  };
}

/**
 * Handle item that is currently hovered / selected with keyboard.
 */

var useScrollToActive = function useScrollToActive(showItems, activeIndex, keyboardNavigation, itemHeight) {
  var listRef = React.useRef(null);

  // Scroll to active element on keyboard navigation
  useIsomorphicLayoutEffect(function () {
    if (!keyboardNavigation) return;
    var halfOfShowItems = Math.round((showItems - 1) / 2);

    // istanbul ignore next: doesn't work in jsdom?
    if (activeIndex > -1 && listRef.current) {
      listRef.current.scrollTop = (activeIndex - halfOfShowItems) * itemHeight;
    }
  }, [showItems, activeIndex, listRef]);
  return {
    listRef: listRef
  };
};

/**
 * Jumps to entry starting with letters typed by user.
 * Typed letters time out after 1s since last input.
 *
 * @param opened Whether list is opened or not
 * @param items List items
 * @param setActive Callback for setting active list item
 * @param setKeyboardNavigation Callback for activating keyboard navigation mode
 */
function useJumpToLetter(opened, items, setActive, setKeyboardNavigation) {
  var enteredKeys = React.useRef('');
  var lastKey = React.useRef(0);

  // Set active item on keyboard list navigation
  React.useEffect(function () {
    var handleKeyDown = function handleKeyDown(e) {
      // If pressed key was a special key (e.g. ArrowLeft), ignore event
      // istanbul ignore next
      if (e.key.length !== 1) {
        // Reset key timer
        lastKey.current = 0;
        return;
      }
      setKeyboardNavigation(true);
      var letter = e.key[0];
      var t = Date.now();

      // User has 1s between keystrokes before we reset search string
      if (t - lastKey.current < 1000) {
        // Append letter to search
        enteredKeys.current += letter;
      } else {
        // Replace search string with letter
        enteredKeys.current = letter;
      }
      lastKey.current = t;
      var search = enteredKeys.current.toLowerCase();
      var match = items.find(function (item) {
        return item.name.toLowerCase().startsWith(search);
      });
      if (match) setActive(match.value);
    };

    // istanbul ignore next
    if (opened) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      // istanbul ignore next
      document.removeEventListener('keydown', handleKeyDown);
    }
    return function () {
      return document.removeEventListener('keydown', handleKeyDown);
    };
  }, [opened, items, setActive]);
}
function ListItems(props) {
  var _headerRef$current$of, _headerRef$current;
  var _props$enableJumpToLe = props.enableJumpToLetter,
    enableJumpToLetter = _props$enableJumpToLe === undefined ? false : _props$enableJumpToLe,
    _props$showItems = props.showItems,
    showItems = _props$showItems === undefined ? 5 : _props$showItems,
    highlightValue = props.highlightValue,
    id = props.id,
    items = props.items,
    onSelect = props.onSelect,
    onClose = props.onClose,
    onActiveChange = props.onActiveChange,
    _props$opened = props.opened,
    opened = _props$opened === undefined ? false : _props$opened,
    selected = props.selected,
    _props$offset = props.offset,
    offset = _props$offset === undefined ? 0 : _props$offset,
    _props$disableFocusTr = props.disableFocusTransfer,
    disableFocusTransfer = _props$disableFocusTr === undefined ? false : _props$disableFocusTr,
    color = props.color,
    onKeyboardNavigateTopOut = props.onKeyboardNavigateTopOut,
    _props$disableBorders = props.disableBorders,
    disableBorders = _props$disableBorders === undefined ? false : _props$disableBorders,
    boxShadow = props.boxShadow,
    highlightSelected = props.highlightSelected,
    ariaMultiselectable = props['aria-multiselectable'],
    ariaLabelledby = props['aria-labelledby'],
    ariaLabel = props['aria-label'],
    _props$role = props.role,
    role = _props$role === undefined ? 'listbox' : _props$role,
    activateFirstItem = props.activateFirstItem,
    _props$selectWithSpac = props.selectWithSpacebar,
    selectWithSpacebar = _props$selectWithSpac === undefined ? true : _props$selectWithSpac,
    renderItem = props.renderItem,
    disableActive = props.disableActive;
    props.maxHeight;
    var _props$listItemRole = props.listItemRole,
    listItemRole = _props$listItemRole === undefined ? 'option' : _props$listItemRole,
    _props$position = props.position,
    position = _props$position === undefined ? 'absolute' : _props$position,
    variantProp = props.variant,
    _props$containerProps = props.containerProps,
    containerProps = _props$containerProps === undefined ? {} : _props$containerProps,
    sizeProp = props.size,
    header = props.header,
    rest = _objectWithoutProperties(props, _excluded);
  var _useHighlightActive = useHighlightActive(opened, items, selected, onSelect, onClose, {
      disableFocusTransfer: disableFocusTransfer,
      onKeyboardNavigateTopOut: onKeyboardNavigateTopOut,
      onActiveChange: onActiveChange,
      activateFirstItem: activateFirstItem,
      selectWithSpacebar: selectWithSpacebar,
      disableActive: disableActive
    }),
    active = _useHighlightActive.active,
    handleMouseMove = _useHighlightActive.handleMouseMove,
    keyboardNavigation = _useHighlightActive.keyboardNavigation,
    setActive = _useHighlightActive.setActive,
    setKeyboardNavigation = _useHighlightActive.setKeyboardNavigation,
    activeRef = _useHighlightActive.activeRef;
  var theme = useTheme();
  var headerRef = React.useRef(null);
  var variant = variantProp || theme.variant;
  var size = variant === 'condensed' ? 's' : sizeProp !== null && sizeProp !== undefined ? sizeProp : theme.preferredComponentSize;
  var itemHeight = variant === 'condensed' ? theme.listItems.web.sizings.s.height : theme.listItems.web.sizings[size].height;
  var maxItems = items.length <= showItems ? items.length : showItems;
  var maxHeight = maxItems * itemHeight + 2 + ((_headerRef$current$of = (_headerRef$current = headerRef.current) === null || _headerRef$current === undefined ? undefined : _headerRef$current.offsetHeight) !== null && _headerRef$current$of !== undefined ? _headerRef$current$of : 0);
  var duration = maxItems * 10 + 150;
  var hidden = !opened || items.length <= 0;
  var showScroll = items.length > showItems;
  useJumpToLetter(opened && enableJumpToLetter, items, setActive, setKeyboardNavigation);
  var _useScrollToActive = useScrollToActive(showItems, getActiveIndex(items)(active), keyboardNavigation, itemHeight),
    listRef = _useScrollToActive.listRef;
  useEffect(function () {
    if (listRef.current) {
      var list = listRef.current;
      var overflow = showScroll ? 'auto' : 'hidden';
      var scrollToActiveItem = function scrollToActiveItem() {
        var active = activeRef.current;
        if (active && overflow !== 'hidden') {
          var y = active.offsetTop - (headerRef.current ? headerRef.current.offsetHeight : 0);

          // istanbul ignore next
          if (list.scrollTo) {
            list.scrollTo({
              top: y
            });
          }
        }
      };
      var _listener = function listener() {
        list.style.overflow = overflow;
        scrollToActiveItem();
        list.removeEventListener('transitionrun', _listener);
      };
      list.addEventListener('transitionrun', _listener);
      return function () {
        return list.removeEventListener('transitionrun', _listener);
      };
    }
  }, [listRef, opened, showScroll, items, itemHeight, maxHeight, activeRef]);
  var renderListItem = function renderListItem(item, idx) {
    var delay = opened ? duration / maxItems * (idx < showItems ? idx : showItems) : 0;

    // we want this undefined if not
    var isSelected = selected ? Array.isArray(selected) ? selected.includes(item.value) : selected === item.value : undefined;
    var isActive = active === item.value;
    return /*#__PURE__*/React.createElement(ListItem, {
      id: isActive ? "".concat(id, "-").concat(item.value, "-active") : "".concat(id, "-").concat(item.value),
      role: listItemRole,
      disableBorders: disableBorders,
      highlightValue: highlightValue,
      highlightSelected: highlightSelected,
      borderRadius: rest.borderRadius,
      key: idx,
      onSelect: onSelect,
      onMouseMove: handleMouseMove,
      active: isActive,
      focusVisible: disableFocusTransfer ? keyboardNavigation : undefined,
      selected: isSelected,
      delay: delay,
      value: item.value,
      icon: item.icon,
      activeRef: isActive ? activeRef : undefined,
      tabIndex: isActive && opened && !disableFocusTransfer ? 0 : -1,
      "aria-posinset": idx + 1,
      "aria-setsize": items.length,
      "aria-multiselectable": ariaMultiselectable,
      color: color,
      size: size,
      variant: variant
    }, renderItem !== undefined ? renderItem(item) : item.name);
  };
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(Box, _extends({
    position: position,
    width: "100%"
  }, containerProps), /*#__PURE__*/React.createElement(container, _extends({
    id: id,
    "data-testid": "elemental-ListItems",
    ref: listRef,
    $maxHeight: px2rem(maxHeight),
    $transitionDuration: duration,
    $offset: offset,
    $color: color || theme.listItems.web.color,
    $disableBorders: disableBorders,
    "data-hidden": !opened,
    boxShadow: boxShadow || theme.listItems.web.boxShadow
  }, rest), header && /*#__PURE__*/React.createElement(ListItemsHeader, {
    title: header.title,
    description: header.description,
    disableBorders: disableBorders,
    icon: header.icon,
    color: color,
    ref: headerRef,
    size: size
  }), /*#__PURE__*/React.createElement(list, {
    id: "".concat(id, "-list"),
    role: role,
    "aria-hidden": !opened,
    "aria-multiselectable": ariaMultiselectable,
    tabIndex: -1,
    "aria-labelledby": ariaLabelledby,
    "aria-label": ariaLabel
  }, items.map(renderListItem))), !disableBorders && /*#__PURE__*/React.createElement(appender, {
    $offset: offset,
    $color: color || theme.listItems.web.color,
    $borderRadius: props.borderRadius,
    hidden: hidden
  })));
}

export { ListItems };
